import { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import {
  getCoachDashboardStats,
  getCoachSchedule,
  ICoachDashboardStats,
  ICoachScheduleEvent
} from 'API/coach-dashboard'
import { teamAccentColor } from 'components/TeamCard/team-card-status-theme'
import { useOrgTeamTree } from 'components/DashboardBanners/OrgTeamSwitcher/use-org-team-tree'
import { formatEventTime, formatEventTime24 } from 'utils/schedule'
import type { CalendarEvent, DashboardData, QuickStat, TeamFilter, WorkoutItem } from './mock-data'
import { DATE_KEY } from './calendar-layout'
import { getWeekDays } from './week-utils'

// The coach half of the play dashboard. Where the athlete's widgets are scoped by
// their own profile, a coach's are scoped by the context they picked in the
// header — all organizations, one organization, or one Team inside it — so every
// number here is re-read from the server on each switch.
//
// The page used to filter one fixed blob by organization in the browser. That
// cannot express "all organizations" and cannot narrow to a Team at all, which is
// what this ticket is about.

/** A metric with nothing behind it reads as a dash, never as a zero. */
const DASH = '—'

export interface UseCoachDashboardDataResult {
  data: DashboardData
  /** The numbers are being re-read for a newly selected organization or Team. */
  isLoading: boolean
  /** Set when the context could not be read — distinct from "this context is empty". */
  error: string | null
}

/** The stats currently held, and the context they belong to. */
interface LoadedStats {
  key: string
  /** Whether that context was a single Team — the tile labels describe it. */
  isTeamScope: boolean
  stats: ICoachDashboardStats
  error: string | null
}

/** The events currently held, and the context *and window* they belong to. */
interface LoadedSchedule {
  key: string
  /**
   * The same context without the window, so the page can ask "are these events
   * for the Team now selected?" without also asking "and for the week now
   * showing?" — the first decides the page spinner, the second must not.
   */
  scope: string
  schedule: ICoachScheduleEvent[]
  error: string | null
}

/** The window the calendar shows, when it has not said yet. */
const defaultRange = (): { from: string; to: string } => {
  const week = getWeekDays()
  return {
    from: dayjs(week[0].iso).startOf('day').toISOString(),
    to: dayjs(week[6].iso).endOf('day').toISOString()
  }
}

/**
 * Identifies a context, so "what is on screen" can be compared against "what was
 * asked for". The date range is part of it: navigating the calendar to another
 * month is as much a change of what is displayed as switching Team is.
 */
const scopeKey = (organizationId: number | null, teamId: number | null, from: string, to: string): string =>
  `${organizationId ?? 'all'}:${teamId ?? 'all'}:${from}:${to}`

const EMPTY_STATS: ICoachDashboardStats = { activeTeams: null, upcoming: 0, completion: null }

const buildQuickStats = (stats: ICoachDashboardStats, isTeamScope: boolean): QuickStat[] => [
  {
    id: 'teams',
    // Null comes back when one Team is selected: counting the Team already on
    // screen says nothing, so the tile steps aside instead of reporting "1".
    value: stats.activeTeams === null ? DASH : String(stats.activeTeams),
    label: 'ACTIVE TEAMS',
    delta: isTeamScope ? 'Viewing one team' : 'Across your organizations',
    iconKey: 'teams',
    accent: '#FFA500'
  },
  {
    id: 'upcoming',
    value: String(stats.upcoming),
    label: 'UPCOMING',
    delta: 'Scheduled ahead',
    iconKey: 'upcoming',
    accent: '#F687B3'
  },
  {
    id: 'completion',
    value: stats.completion === null ? DASH : `${stats.completion}%`,
    label: 'COMPLETION',
    delta: stats.completion === null ? 'Nothing assigned yet' : 'Drills completed',
    iconKey: 'completion',
    accent: '#A8E46E'
  }
]

/**
 * The coach dashboard's data for one context.
 *
 * `enabled` is false for an athlete: the hook still runs (hooks cannot be called
 * conditionally) but issues no requests and returns the empty shape.
 */
export const useCoachDashboardData = (
  organizationId: number | null,
  teamId: number | null,
  enabled: boolean,
  range: { from: string; to: string } | null
): UseCoachDashboardDataResult => {
  const { organizations } = useOrgTeamTree()
  const [loadedStats, setLoadedStats] = useState<LoadedStats | null>(null)
  const [loadedSchedule, setLoadedSchedule] = useState<LoadedSchedule | null>(null)
  // A slow answer for the previous Team must not land after the new one — that is
  // exactly the mismatch between banner and content this ticket forbids. One
  // counter per request kind, so a calendar page-turn cannot discard a newer
  // stats answer or the reverse.
  const statsSeq = useRef(0)
  const scheduleSeq = useRef(0)

  const { from, to } = range ?? defaultRange()
  const statsKey = scopeKey(organizationId, teamId, '', '')
  const scheduleKey = scopeKey(organizationId, teamId, from, to)

  // Derived, not stored. A flag set inside the effect is only correct from the
  // commit *after* the switch, because effects run after paint — which leaves one
  // painted frame showing the new Team's banner over the old Team's numbers with
  // nothing to say so. Comparing the context on screen against the context asked
  // for is right on the very first render instead.
  //
  // One flag for the whole page, the way a list page reads one `isFetchingList`.
  // It covers every wait the first paint depends on, so the page never has to
  // combine several:
  //  - not yet `enabled`: the organization/Team tree is still being read, and the
  //    dashboard cannot ask for anything until it says which context to ask about;
  //  - the numbers, and the calendar's events, for the context now selected.
  // Leaving either read out painted the page in stages — tiles first, calendar a
  // moment later — which reads as loading twice.
  //
  // The schedule is compared on scope alone: turning a calendar page re-reads it
  // for another window, and blanking the page for that would also throw away the
  // mode and date the coach just picked. That window arrives under the calendar
  // it belongs to.
  const isLoading = !enabled || loadedStats?.key !== statsKey || loadedSchedule?.scope !== statsKey

  const scope = useMemo(
    () => ({ organizationId: organizationId ?? undefined, teamId: teamId ?? undefined }),
    [organizationId, teamId]
  )

  // The numbers do not depend on which week the calendar is showing, so paging
  // through the calendar must not re-run them — the completion metric is the most
  // expensive query in the feature, and re-running it would also veil the Quick
  // Stats every time the viewer turned a page.
  useEffect(() => {
    if (!enabled) return
    const seq = ++statsSeq.current
    void getCoachDashboardStats(scope)
      .then(stats => {
        if (seq !== statsSeq.current) return
        setLoadedStats({ key: statsKey, isTeamScope: teamId !== null, stats, error: null })
      })
      .catch(() => {
        if (seq !== statsSeq.current) return
        // An unreadable organization renders identically to one with no teams, so
        // the failure is carried through and said out loud rather than passed off
        // as emptiness.
        setLoadedStats({
          key: statsKey,
          isTeamScope: teamId !== null,
          stats: EMPTY_STATS,
          error: 'We could not load this organization or team. Try another, or refresh.'
        })
      })
  }, [enabled, scope, statsKey, teamId])

  useEffect(() => {
    if (!enabled) return
    const seq = ++scheduleSeq.current
    void getCoachSchedule(from, to, scope)
      .then(schedule => {
        if (seq !== scheduleSeq.current) return
        setLoadedSchedule({ key: scheduleKey, scope: statsKey, schedule, error: null })
      })
      .catch(() => {
        if (seq !== scheduleSeq.current) return
        setLoadedSchedule({
          key: scheduleKey,
          scope: statsKey,
          schedule: [],
          error: 'We could not load the schedule for this window.'
        })
      })
  }, [enabled, from, scheduleKey, statsKey, scope, to])

  const loaded = loadedStats
  const stats = loadedStats?.stats ?? EMPTY_STATS
  const schedule = loadedSchedule?.schedule ?? []

  const data = useMemo<DashboardData>(() => {
    const colorByOrg = new Map(organizations.map(org => [org.id, teamAccentColor(org.id, org.primaryColor)]))
    const nameByOrg = new Map(organizations.map(org => [org.id, org.name]))
    const accentFor = (orgId: number): string => colorByOrg.get(orgId) ?? teamAccentColor(orgId)

    // The mobile chip row scopes by organization, so the chips are the coach's
    // organizations — the header switcher writes the same selection.
    const teamFilters: TeamFilter[] = organizations.map(org => ({
      id: String(org.id),
      name: org.name,
      color: accentFor(org.id)
    }))

    const workouts: WorkoutItem[] = schedule.map(event => ({
      id: String(event.id),
      title: event.name || 'Practice',
      startTime: formatEventTime(event.startTime),
      endTime: formatEventTime(event.endTime),
      location: event.location || '',
      orgId: String(event.organizationId),
      date: event.scheduledDate ? dayjs(event.scheduledDate).format('YYYY-MM-DD') : ''
    }))

    const calendarEvents: CalendarEvent[] = schedule.map(event => ({
      id: String(event.id),
      // From `startTime`, not `scheduledDate`: the latter is stored as local
      // midnight, so for a viewer in another timezone the two can fall on
      // different days and the card would be filed under one and drawn at the
      // other's clock time.
      date: dayjs(event.startTime || event.scheduledDate).format(DATE_KEY),
      start: formatEventTime24(event.startTime),
      end: formatEventTime24(event.endTime),
      title: event.name || 'Practice',
      location: event.location || '',
      orgId: String(event.organizationId),
      teamTag: {
        // An event can span Teams; the card has room for one label, so it names
        // the first and falls back to the organization when the join is empty.
        label: event.teams[0]?.name || nameByOrg.get(event.organizationId) || 'Team',
        color: accentFor(event.organizationId)
      }
    }))

    return {
      // The page prefers the logged-in user's own name for the greeting.
      greetingName: '',
      // A coach has no drill streak of their own; the banner renders its zero
      // state rather than a fabricated one.
      rallyDay: {
        streakDays: 0,
        weekDots: [],
        nextUpTime: '',
        routineTitle: '',
        routineSubtitle: '',
        routineDurationMin: 0,
        routineReps: 0,
        equipment: '',
        drillCount: 0,
        todayDrills: []
      },
      assignedPlans: [],
      orgs: [],
      team: { name: '', coach: '', level: '', nextEvent: '', nextEventTime: '', color: '#3b82f6', initials: '' },
      notifications: [],
      // Read off the loaded context, not the selected one: mid-switch the two
      // differ, and pairing this scope's label with the last scope's number is
      // the same mismatch the veil exists to hide.
      quickStats: buildQuickStats(stats, loaded?.isTeamScope ?? teamId !== null),
      teamFilters,
      workouts,
      todos: [],
      nextUpCards: [],
      calendarEvents
    }
  }, [loaded, organizations, schedule, stats, teamId])

  // Only a failure that describes the context now on screen; a stale one belongs
  // to a context the viewer has already navigated away from.
  const error =
    (loadedStats?.key === statsKey ? loadedStats.error : null) ??
    (loadedSchedule?.key === scheduleKey ? loadedSchedule.error : null)

  return { data, isLoading, error }
}

export default useCoachDashboardData
